import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
import { getRoleThemeClasses } from '../../logic/uiHelpers';
import { dataLayer } from '../../../data/dataLayer';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  totalSteps?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  totalSteps = 2,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const theme = getRoleThemeClasses(role);
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Ik geef je graag een rondleiding door de app.`
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-74" data-mp="1yh9lr4up6igz">
      
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-6 relative" data-id="element-75" data-mp="2qz46c11bw48d">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-76" data-mp="2m7celn6frsgn">
          
          <DjayNarrator emotion={config.emotion} size="lg" data-id="element-77" data-mp="kallki0nbh1u" />
        </motion.div>
        <div className="mt-3 w-full max-w-[260px]" data-id="element-78" data-mp="p8dffhkupd8o">
          <DjayBubble message={config.message} position="top" colorScheme={theme.colorScheme} autoDismissMs={0} data-id="element-79" data-mp="aka6q0ffbng6" />
          
        </div>
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-6 sm:p-8 shadow-lg border ${theme.border} relative overflow-hidden`} data-id="element-80" data-mp="fh0lmkfhsd00">
        
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${theme.gradient}`} data-id="element-81" data-mp="5knw7yqzclb2" />
        

        <OnboardingProgress currentStep={0} totalSteps={totalSteps} role={role} data-id="element-82" data-mp="3nr27tr1s0j6a" />
        

        {role === 'learner' && <div className="space-y-6" data-id="element-83" data-mp="3sou0woe5qbc2">
            <div className="text-center" data-id="element-84" data-mp="3spvm3low0e7i">
              <h2 className="text-2xl font-black text-gray-900 mb-2" data-id="element-85" data-mp="1lynsmzqm9stk">
                Klaar voor de start? 🚀
              </h2>
              <p className="text-gray-600 font-medium" data-id="element-86" data-mp="1qwxolck60xg8">
                Je gaat oefenen op het niveau van{' '}
                <span className="font-bold text-orange-600" data-id="element-87" data-mp="3bgt0qqcbtydo">
                  groep {groupLevel}
                </span>
                .
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 space-y-4" data-id="element-88" data-mp="3l6cooaewcife">
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-89" data-mp="3gf4a6zypm6vd">
              
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-90" data-mp="3pa25gdpbxvpv">
                  <span className="text-xl" data-id="element-91" data-mp="3081w2desloa">🗺️</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-92" data-mp="29qnyla6cj3ad">
                  Ontdek waar je alles kunt vinden in de app
                </p>
              </motion.div>
              <motion.div className="flex items-center gap-3" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-93" data-mp="24txm0wtr2ptp">
              
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-94" data-mp="15u51l50g0bcp">
                  <span className="text-xl" data-id="element-95" data-mp="19skvztyusm05">⭐</span>
                </div>
                <p className="text-sm font-medium text-orange-900" data-id="element-96" data-mp="d5zi0ll4kseg">
                  Zie hoe je XP en nieuwe looks verdient
                </p>
              </motion.div>
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-97" data-mp="m305mq0demgu">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-98" data-mp="qwcn8g6mtt03">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-99" data-mp="3qijfrr2zz5ii">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-100" data-mp="3qhi0faey7wuz">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-101" data-mp="3ujd2wi8m8gac">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-102" data-mp="3mzas83jx6kaj" />
                </div>
                <span data-id="element-103" data-mp="3i33jy441wwpf">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-104" data-mp="3dznohb3px2eb">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-105" data-mp="391vxcjawdkb6">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-106" data-mp="342sxdd9ry4a2" />
                </div>
                <span data-id="element-107" data-mp="2zbawep7r43p3">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-108" data-mp="2ucpr4bdn6tts">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-109" data-mp="2uczju6q2nf36">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-110" data-mp="2q9babihvq9fa" />
                </div>
                <span data-id="element-111" data-mp="2le4cgrbajyya">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-112" data-mp="26pch3ot7gyf9">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-113" data-mp="26ob25992jrs9">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-114" data-mp="2bpqod4mzg4c8">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-115" data-mp="1wreqwvrscrk0">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-116" data-mp="1wt9g4ma3b5tw">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-117" data-mp="2pfu4ddv9r1t8" />
                </div>
                <span data-id="element-118" data-mp="2pi6ljot7d858">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-119" data-mp="2uedulqgwazre">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-120" data-mp="2fqsnllgwgge7">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-121" data-mp="2knsutzphjp6w" />
                </div>
                <span data-id="element-122" data-mp="38dn98lpgdlt9">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-123" data-mp="3d5epo3ewab5o">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-124" data-mp="3d6pvs9vtbuzy">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-125" data-mp="11o3427lv6zsc" />
                </div>
                <span data-id="element-126" data-mp="wp8ln6rt7d8r">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8 flex flex-col gap-3" data-id="element-127" data-mp="1bibrigw1g2jn">
          {role === 'learner' ? <>
              <Button onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: true,
                tourCompleted: false
              });
            } catch (e) {}
            onNext();
          }} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-128" data-mp="16hxt7qk8o6yt">
              
                Start de rondleiding
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-129" data-mp="235u54i4qee3p" />
              </Button>
              <Button variant="ghost" onClick={async () => {
            try {
              const prefs = await dataLayer.getLearnerPreferences();
              await dataLayer.saveLearnerPreferences({
                ...prefs,
                pendingAppTour: false,
                tourCompleted: true
              });
            } catch (e) {}
            onNext();
          }} className="w-full text-slate-500 hover:text-slate-700" data-id="element-130" data-mp="1z4s2rsfiplxi">
              
                Sla over
              </Button>
            </> : <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${theme.buttonBg}`} data-id="element-131" data-mp="f8uo585wd909">
            
              Verder
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-132" data-mp="katrwj5ju7xq" />
            </Button>}
        </div>
      </div>
    </motion.div>;
}